import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ApiService } from '../../../core/service/api.service';
import { SharedService } from '../../../core/service/shared.service';
import { lastValueFrom } from 'rxjs';

export interface SyncDialogConfig {
  /** Dialog heading. Default: "Sync Society" */
  title?: string;
  /** Input label. Default: "Society Name" */
  label?: string;
  /** Input placeholder. Default: "Enter society name" */
  placeholder?: string;
  /** Query-param key sent to the scheduler API. Default: "society" */
  paramName?: string;
  /** Scheduler API path. Default: "/api/master/callHmbWiseFieldForceStaff" */
  apiUrl?: string;
  /** Extra fixed query params sent along with the input value. */
  extraParams?: { [key: string]: string };
}

@Component({
  selector: 'app-sync-dialog',
  templateUrl: './sync-dialog.component.html',
  styleUrls: ['./sync-dialog.component.scss']
})
export class SyncDialogComponent {
  /** Bound to the single text input in the template. */
  syncValue: string = '';
  isLoading: boolean = false;

  title: string;
  label: string;
  placeholder: string;
  paramName: string;
  apiUrl: string;
  extraParams: { [key: string]: string };

  constructor(
    public dialogRef: MatDialogRef<SyncDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SyncDialogConfig,
    private apiService: ApiService,
    private sharedService: SharedService
  ) {
    const cfg = data || {};
    this.title = cfg.title || 'Sync Society';
    this.label = cfg.label || 'Society Name';
    this.placeholder = cfg.placeholder || 'Enter society name';
    this.paramName = cfg.paramName || 'society';
    this.apiUrl = cfg.apiUrl || '/api/master/callHmbWiseFieldForceStaff';
    this.extraParams = cfg.extraParams || {};
  }

  /** Kept for backward compatibility with existing callers/specs. */
  get societyName(): string {
    return this.syncValue;
  }
  set societyName(value: string) {
    this.syncValue = value;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  async onSync(): Promise<void> {
    const trimmed = this.syncValue ? this.syncValue.trim() : '';
    if (!trimmed) {
      return; // guard against empty sync
    }

    this.isLoading = true;
    try {
      const params = { ...this.extraParams, [this.paramName]: trimmed };
      const syncResponse$ = this.apiService.getScheduler(this.apiUrl, params);
      await lastValueFrom(syncResponse$);

      this.sharedService.showNotification('snackbar-success', 'Sync completed successfully', 'top', 'right');
      this.dialogRef.close(trimmed);
    } catch (error) {
      console.error('Error syncing:', error);
      this.sharedService.showNotification('snackbar-danger', 'Error syncing data', 'top', 'right');
    } finally {
      this.isLoading = false;
    }
  }
}
